import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { Loader2, Bot } from 'lucide-react';
import { useAuth } from './hooks/useAuth';
import { GoogleSignInSection } from './GoogleSignInButton';
import { MARKETING_URL } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

const registerSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
  acceptTerms: z.boolean().refine(val => val === true, 'You must accept the terms')
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"]
});

type RegisterForm = z.infer<typeof registerSchema>;

// The legal pages live in the Astro marketing zone, not in this SPA, which has no /terms route.
// Empty MARKETING_URL (local dev) falls back to a relative path.
const TERMS_HREF = `${MARKETING_URL}/terms`;
const PRIVACY_HREF = `${MARKETING_URL}/privacy`;

export const RegisterPage = () => {
  const { register, googleSignIn, isLoading } = useAuth();
  
  const form = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false
    }
  });

  const onSubmit = (data: RegisterForm) => {
    register({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      password: data.password
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50/60 dark:bg-slate-950 py-8 px-4 sm:px-6 transition-colors">
      <div className="max-w-[420px] w-full space-y-4">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="p-1.5 rounded-xl bg-primary/10 text-primary">
              <Bot className="h-6 w-6" />
            </div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">OmnibizChat</h1>
          </div>
          <h2 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">
            Create your account
          </h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-primary hover:text-primary/80 transition-colors">
              Sign in here
            </Link>
          </p>
        </div>

        <Card className="p-5 sm:p-6 shadow-sm border-slate-200/80 dark:border-slate-800/80 rounded-2xl bg-white dark:bg-slate-900">
          <GoogleSignInSection
            context="signup"
            onCredential={(credential) => googleSignIn({ credential })}
            disabled={isLoading}
            footnote={
              <p className="text-center text-[11px] text-slate-500 dark:text-slate-400">
                By continuing with Google you agree to the{' '}
                <a href={TERMS_HREF} className="text-primary hover:text-primary/80 underline">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href={PRIVACY_HREF} className="text-primary hover:text-primary/80 underline">
                  Privacy Policy
                </a>
                .
              </p>
            }
          />

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3.5">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="firstName" className="text-xs font-medium text-slate-700 dark:text-slate-300">First Name</Label>
                <Input
                  {...form.register('firstName')}
                  className="h-9 px-3 text-xs rounded-lg border-slate-200 dark:border-slate-800"
                  placeholder="First name"
                />
                {form.formState.errors.firstName && (
                  <p className="text-error text-xs mt-0.5">
                    {form.formState.errors.firstName.message}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="lastName" className="text-xs font-medium text-slate-700 dark:text-slate-300">Last Name</Label>
                <Input
                  {...form.register('lastName')}
                  className="h-9 px-3 text-xs rounded-lg border-slate-200 dark:border-slate-800"
                  placeholder="Last name"
                />
                {form.formState.errors.lastName && (
                  <p className="text-error text-xs mt-0.5">
                    {form.formState.errors.lastName.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="email" className="text-xs font-medium text-slate-700 dark:text-slate-300">Email</Label>
              <Input
                {...form.register('email')}
                type="email"
                className="h-9 px-3 text-xs rounded-lg border-slate-200 dark:border-slate-800"
                placeholder="Enter your email"
              />
              {form.formState.errors.email && (
                <p className="text-error text-xs mt-0.5">
                  {form.formState.errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="password" className="text-xs font-medium text-slate-700 dark:text-slate-300">Password</Label>
              <Input
                {...form.register('password')}
                type="password"
                className="h-9 px-3 text-xs rounded-lg border-slate-200 dark:border-slate-800"
                placeholder="Create a password"
              />
              {form.formState.errors.password && (
                <p className="text-error text-xs mt-0.5">
                  {form.formState.errors.password.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="confirmPassword" className="text-xs font-medium text-slate-700 dark:text-slate-300">Confirm Password</Label>
              <Input
                {...form.register('confirmPassword')}
                type="password"
                className="h-9 px-3 text-xs rounded-lg border-slate-200 dark:border-slate-800"
                placeholder="Confirm your password"
              />
              {form.formState.errors.confirmPassword && (
                <p className="text-error text-xs mt-0.5">
                  {form.formState.errors.confirmPassword.message}
                </p>
              )}
            </div>

            <div className="flex items-start space-x-2 pt-0.5">
              <Checkbox {...form.register('acceptTerms')} id="accept-terms" className="mt-0.5 rounded" />
              <Label htmlFor="accept-terms" className="text-[11px] leading-tight text-slate-600 dark:text-slate-400 font-normal">
                I agree to the{' '}
                <a href={TERMS_HREF} className="text-primary hover:text-primary/80 underline font-medium">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href={PRIVACY_HREF} className="text-primary hover:text-primary/80 underline font-medium">
                  Privacy Policy
                </a>
              </Label>
            </div>
            {form.formState.errors.acceptTerms && (
              <p className="text-error text-xs">
                {form.formState.errors.acceptTerms.message}
              </p>
            )}

            <div className="pt-1">
              <Button 
                type="submit" 
                className="w-full h-10 py-2.5 px-4 rounded-lg text-sm font-medium shadow-sm hover:shadow transition-all flex items-center justify-center gap-2" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create account'
                )}
              </Button>
            </div>
          </form>

          <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800/80">
            <div className="flex items-center justify-center gap-4 text-[11px] text-slate-500 dark:text-slate-400">
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                Multi-Channel AI
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                Live Stock Sync
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                Omni Inbox
              </span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};